import { getTheme } from '@golfauth/utils';
import { supabase } from './supabase';
import { Browser } from '@capacitor/browser';

// User-facing Core SPA lives at golfauth.com/core/ (Fitting site's build:core
// bundle is copied into dist/core). app.golfauth.com is a SEPARATE Vercel
// project outside this repo — do not point Capacitor users there.
// Trailing slash matters: /core → 308 to /core/, and the redirect chain
// with a query string (?theme=dark, ?token=...) can trip the Fitting SW's
// navigation-fallback handler on some browsers → ERR_FAILED. Ship the
// canonical URL directly so no redirect is emitted.
const CAPACITOR_CORE_FALLBACK = 'https://golfauth.com/core/';

const isLoopbackCoreUrl = (u: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(u);

function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { Capacitor?: { getPlatform?: () => string } };
  const p = w.Capacitor?.getPlatform?.();
  return p === 'ios' || p === 'android';
}

/**
 * Core app URL (My Bag). Combined Netlify: Fitting at /, Core static at /core/.
 * Override with VITE_GOLFAUTH_CORE_APP_URL when Core is on another host (e.g. https://app.golfauth.com).
 * Local dev: unset → same origin + /core (Vite proxies /core to the Core dev server on 5180).
 *
 * Capacitor native: a loopback env value is unreachable from the device; fall
 * back to the deployed Core host so the in-app browser overlay has a valid
 * target.
 */
function getCoreAppBaseUrl(): string {
  const envRaw = import.meta.env.VITE_GOLFAUTH_CORE_APP_URL;
  const envTrimmed = typeof envRaw === 'string' && envRaw.trim() ? envRaw.trim().replace(/\/$/, '') : '';

  if (isCapacitorNative()) {
    if (envTrimmed && (envTrimmed.startsWith('http://') || envTrimmed.startsWith('https://')) && !isLoopbackCoreUrl(envTrimmed)) {
      return envTrimmed;
    }
    return CAPACITOR_CORE_FALLBACK;
  }

  if (envTrimmed) {
    if (envTrimmed.startsWith('http://') || envTrimmed.startsWith('https://')) return envTrimmed;
    if (envTrimmed.startsWith('/') && typeof window !== 'undefined') {
      return `${window.location.origin.replace(/\/$/, '')}${envTrimmed}`;
    }
    return envTrimmed;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin.replace(/\/$/, '')}/core/`;
  }
  return 'http://localhost:5180/';
}

/** `?intent=` values Core understands — all open Snap your bag. */
export type CoreIntent = 'bag-scan' | 'first-club' | 'valuation';

function fittingOriginForLogin(): string {
  const fromEnv = import.meta.env.VITE_APP_BASE_URL;
  if (fromEnv) return String(fromEnv).replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:5173';
}

/** Is `base` the same origin as the page we are currently on? */
function isSameOrigin(base: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(base).origin === window.location.origin;
  } catch {
    return false;
  }
}

async function navigateExternal(url: string): Promise<void> {
  if (isCapacitorNative()) {
    try {
      await Browser.open({ url, presentationStyle: 'popover' });
      return;
    } catch (err) {
      console.error('[goToCore] Capacitor Browser.open failed; falling back to window.open', err);
      window.open(url, '_blank');
      return;
    }
  }
  window.location.href = url;
}

/**
 * Navigate to GolfAuth Core.
 *
 * Same-origin web case (current setup: Fitting at / + Core at /core/ on the
 * same Netlify site) — Supabase session is already shared via origin-scoped
 * localStorage, so we navigate with **no tokens in the URL**. This avoids
 * leaking JWTs into browser history, Referer headers, analytics, and Netlify
 * access logs.
 *
 * Cross-origin / Capacitor — attach `token`/`refresh_token` so the destination
 * can hydrate. On Capacitor we open in an in-app browser (SFSafariViewController
 * on iOS / Custom Tabs on Android) instead of replacing the WebView, so the
 * pro can return to GolfAuth Pro by dismissing.
 */
export function goToCore(): Promise<void> {
  return openCore();
}

/** goToCore + `?intent=`, which Core reads and strips on load
 *  (src/lib/bagScan/intent.ts). Separate export because goToCore is passed
 *  straight to onClick in several places. */
export function goToCoreWithIntent(intent: CoreIntent): Promise<void> {
  return openCore(intent);
}

async function openCore(intent?: CoreIntent): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const base = getCoreAppBaseUrl();
  const u = new URL(base);
  if (intent) u.searchParams.set('intent', intent);

  const sameOrigin = isSameOrigin(base);

  if (session?.access_token && session?.refresh_token) {
    if (!sameOrigin || isCapacitorNative()) {
      // Cross-origin / Capacitor — hydrate the destination via URL.
      // Cookies do not cross origins, so we still forward theme +
      // session tokens here.
      u.searchParams.set('theme', getTheme());
      u.searchParams.set('token', session.access_token);
      u.searchParams.set('refresh_token', session.refresh_token);
    }
    // Same-origin: NO query params. Theme is set via the
    // `.golfauth.com`-scoped cookie the preheader writes, and Core's
    // useAuth.ts strips ?theme= on land anyway. Trailing `?theme=dark`
    // on the URL was also the wedge that stale service workers
    // failed on (TypeError: Failed to fetch on /core/?theme=dark
    // while /core/ worked). Dropping it removes the wedge.
    await navigateExternal(u.toString());
    return;
  }
  const login = new URL('/customer/login', fittingOriginForLogin());
  login.searchParams.set('redirect', '/golfer/core');
  login.searchParams.set('theme', getTheme());
  await navigateExternal(login.toString());
}
